import { useCallback, useMemo, useRef } from 'react'
import { keepPreviousData, useInfiniteQuery, useQueryClient, InfiniteData } from '@tanstack/react-query'

import { graphqlRequest } from '../../../../services/tanstack-query'
import { queryKeys } from '../../../../services/tanstack-query/queryClient'
import { getUsersFiltered, getUsersFilteredWithoutEnergy } from '../../../../graphql/custom-queries'
import { IGetUsersFiltered, IUserFilteredInfo } from '../../../../types/users'
import { IUsersFilter } from '../types'

const USERS_PAGE_SIZE = 20

// The view stores startDate as a UTC timestamp but it means a calendar day, so drop
// the time here - formatting it locally renders the day before for browsers behind UTC.
const toCalendarDate = (value: string) => value.split('T')[0]

// The completion-driven refetch can read the users view before a status change is
// projected and resurrect an optimistically removed row. Tombstones veto a removed
// row in the list it was removed from until the projection catches up; after the
// TTL a server-confirmed reappearance (e.g. the user was deactivated again) shows normally.
const REMOVED_ROW_TTL_MS = 60_000

export interface IUseUsersInfiniteResult {
  users: IUserFilteredInfo[]
  scannedCount?: number
  isLoading: boolean
  isFetchingNewList: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  patchUser: (userId: string, patch: Partial<IUserFilteredInfo>) => void
  removeUser: (userId: string) => void
}

const listKeyFilters = (filters: IUsersFilter) => ({
  status: filters.status,
  name: filters.name,
  locationIds: filters.locationIds,
  teamIds: filters.teamIds,
  labelIds: filters.labelIds,
  inviteStatuses: filters.inviteStatuses,
})

export function useUsersInfinite(filters: IUsersFilter, includeEnergy = false): IUseUsersInfiniteResult {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => queryKeys.users.list({ ...listKeyFilters(filters), energy: includeEnergy }), [filters, includeEnergy])

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      graphqlRequest<IGetUsersFiltered>(includeEnergy ? getUsersFiltered : getUsersFilteredWithoutEnergy, {
        status: filters.status,
        name: filters.name,
        locationIds: filters.locationIds,
        teamIds: filters.teamIds,
        labelIds: filters.labelIds,
        inviteStatuses: filters.inviteStatuses,
        limit: USERS_PAGE_SIZE,
        nextToken: pageParam ?? '',
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.getUsersFiltered.nextToken || undefined,
    placeholderData: keepPreviousData,
  })

  const removedRowsRef = useRef(new Map<string, { status: IUsersFilter['status']; expiresAt: number }>())

  const users = useMemo(() => {
    const removedRows = removedRowsRef.current
    for (const [userId, tombstone] of removedRows) {
      if (tombstone.expiresAt <= Date.now()) {
        removedRows.delete(userId)
      }
    }
    return (query.data?.pages || [])
      .flatMap((page) => page.getUsersFiltered.users || [])
      .filter((user) => user.location && user.team && user.name)
      .filter((user) => removedRows.get(user.id)?.status !== filters.status)
      .map((user) => (user.startDate ? { ...user, startDate: toCalendarDate(user.startDate) } : user))
  }, [query.data, filters.status])

  const lastPage = query.data?.pages?.[query.data.pages.length - 1]

  const mapUsers = useCallback(
    (mapper: (users: IUserFilteredInfo[]) => IUserFilteredInfo[]) => {
      queryClient.setQueryData<InfiniteData<IGetUsersFiltered>>(queryKey, (data) => {
        if (!data) {
          return data
        }
        return {
          ...data,
          pages: data.pages.map((page) => ({
            getUsersFiltered: {
              ...page.getUsersFiltered,
              users: mapper(page.getUsersFiltered.users || []),
            },
          })),
        }
      })
    },
    [queryClient, queryKey]
  )

  const patchUser = useCallback(
    (userId: string, patch: Partial<IUserFilteredInfo>) => {
      mapUsers((pageUsers) => pageUsers.map((user) => (user.id === userId ? { ...user, ...patch } : user)))
    },
    [mapUsers]
  )

  const removeUser = useCallback(
    (userId: string) => {
      removedRowsRef.current.set(userId, { status: filters.status, expiresAt: Date.now() + REMOVED_ROW_TTL_MS })
      mapUsers((pageUsers) => pageUsers.filter((user) => user.id !== userId))
    },
    [mapUsers, filters.status]
  )

  return {
    users,
    scannedCount: lastPage?.getUsersFiltered.scannedCount,
    isLoading: query.isLoading,
    isFetchingNewList: query.isPlaceholderData && query.isFetching,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    patchUser,
    removeUser,
  }
}
